import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, ColDef, RowStyle } from "ag-grid-community";
import { getNames } from "country-list";
import { Box } from "../../components/utils";
import { tableCellStyle, tableEntryStyle } from "../../components/TableComponent";
import { formatToReadableNumber } from "../../utils/currencyRoboto";
import { checkVoid } from "../../utils/transactionUtils";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { currencyTypes } from "../../constants/CompanyCurrencyType";
import { ParsedShareholding } from "../../types/ExcelParsingOnboarding";

const countries = getNames().sort();

type ShareholdingDetailsGridProps = {
  rows: ParsedShareholding[];
  currency?: string;
  onRowsChange?: (rows: ParsedShareholding[]) => void;
};

const sumField = (
  rows: ParsedShareholding[],
  field: keyof ParsedShareholding
) =>
  rows.reduce((sum, row) => {
    const value = Number(row[field]);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

// Accepts a currency code ("INR"), a "CODE - symbol" display string ("INR - ₹"),
// or an already-valid locale tag, and resolves it to a toLocaleString-safe locale.
const resolveCurrencyLocale = (value?: string): string | undefined => {
  if (!value) return undefined;
  const code = value.trim().split(/[\s-]/)[0]?.toUpperCase();
  return code ? currencyTypes[code] : undefined;
};

export default function ShareholdingDetailsGrid({
  rows,
  currency,
  onRowsChange,
}: ShareholdingDetailsGridProps) {
  const fallbackCurrency = getCurrencyType();
  const currencyType = resolveCurrencyLocale(currency) || fallbackCurrency;
  const [rowData, setRowData] = useState<ParsedShareholding[]>(rows);

useEffect(() => {
  setRowData(
    rows.map((row) => ({
      ...row,
      shareholderFDBPercentage:
        row.shareholderFDBPercentage != null
          ? Number((Number(row.shareholderFDBPercentage) * 100).toFixed(2))
          : 0,
      tooltipShareholderFDBPercentage:
        row.shareholderFDBPercentage != null
          ? `${(Number(row.shareholderFDBPercentage) * 100).toFixed(2)}%`
          : "0%",
    }))
  );
}, [rows]);

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<ParsedShareholding>) => {
      if (params.rowIndex == null || !params.data) return;
      setRowData((prev) => {
        const next = [...prev];
        next[params.rowIndex as number] = params.data as ParsedShareholding;
        onRowsChange?.(next);
        return next;
      });
    },
    [onRowsChange]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Shareholder Name",
        field: "shareholderName",
        pinned: "left",
        minWidth: 180,
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Security Type",
        field: "securityType",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Security Class",
        field: "securityClass",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Non-Priced",
        field: "nonPriced",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Number of Shares",
        field: "numberOfShares",
        filter: "agNumberColumnFilter",
        editable: (params) => !params.node.rowPinned,
        cellEditor: "agNumberCellEditor",
        cellStyle: tableCellStyle,
        cellClass: `${tableEntryStyle} whitespace-nowrap`,
        valueFormatter: (p) =>
          formatToReadableNumber({ value: p.value, format: currencyType }),
        tooltipValueGetter: (p) =>
          checkVoid(p.value) ? "" : Number(p.value).toLocaleString(currencyType),
      },
      {
        headerName: "FDB (%)",
        field: "shareholderFDBPercentage",
        filter: "agNumberColumnFilter",
        editable: (params) => !params.node.rowPinned,
        cellEditor: "agNumberCellEditor",
        cellStyle: tableCellStyle,
        cellClass: `${tableEntryStyle} whitespace-nowrap`,
        // tooltipValueGetter: (p) =>
        //   checkVoid(p.value) ? "" : `${(Number(p.value) * 100).toFixed(2)}%`,
        tooltipValueGetter: (p) =>
          checkVoid(p.data?.tooltipShareholderFDBPercentage)
            ? ""
            : p.data?.tooltipShareholderFDBPercentage,
      },
      {
        headerName: "Shareholder Type",
        field: "shareholderType",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Country",
        field: "shareholderCountry",
        editable: (params) => !params.node.rowPinned,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: countries },
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Date of Investment",
        field: "dateOfInvestment",
        filter: "agDateColumnFilter",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
        valueFormatter: (p) => p.value || "-",
      },
      {
        headerName: "Price per Share",
        field: "pricePerShare",
        filter: "agNumberColumnFilter",
        editable: (params) => !params.node.rowPinned,
        cellEditor: "agNumberCellEditor",
        cellStyle: tableCellStyle,
        cellClass: `${tableEntryStyle} whitespace-nowrap`,
        valueFormatter: (p) =>
          formatToReadableNumber({ value: p.value, format: currencyType }),
        tooltipValueGetter: (p) =>
          checkVoid(p.value) ? "" : Number(p.value).toLocaleString(currencyType),
      },
      {
        headerName: "PAN",
        field: "pan",
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 200,
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Address",
        field: "address",
        minWidth: 220,
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
      {
        headerName: "Remarks",
        field: "remarks",
        minWidth: 200,
        editable: (params) => !params.node.rowPinned,
        cellStyle: tableCellStyle,
        cellClass: tableEntryStyle,
      },
    ],
    [currencyType]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      autoHeight: true,
      wrapHeaderText: true,
      minWidth: 130,
      menuTabs: ["filterMenuTab"],
      flex: 1,
      cellStyle: tableCellStyle,
    }),
    []
  );

  const pinnedBottomRowData = useMemo<ParsedShareholding[]>(() => {
    if (rowData.length === 0) return [];
    const totalFDBPercentage = sumField(rowData, "shareholderFDBPercentage");
    return [
      {
        shareholderName: "Total",
        securityType: "",
        numberOfShares: sumField(rowData, "numberOfShares"),
        // Rounding errors from per-row percentages can leave the total a hair
        // off 100 (e.g. 99.99 or 100.02) — snap it to exactly 100 in that case.
        shareholderFDBPercentage:
          Math.abs(totalFDBPercentage - 100) <= 0.2
            ? 100
            : totalFDBPercentage,
        pricePerShare: sumField(rowData, "pricePerShare"),
      },
    ];
  }, [rowData]);

  const getRowStyle = useCallback(
    (params: { node: { rowPinned?: string | null } }): RowStyle | undefined =>
      params.node.rowPinned
        ? { fontWeight: 600, background: "#f5f5f4" }
        : undefined,
    []
  );

  return (
    <Box
      style={{ height: "420px" }}
      className="w-full max-h-full overflow-x-auto ag-theme-material"
    >
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        pinnedBottomRowData={pinnedBottomRowData}
        getRowStyle={getRowStyle}
        onCellValueChanged={onCellValueChanged}
        tooltipShowDelay={0}
        domLayout="normal"
        animateRows
      />
    </Box>
  );
}
